/**
 * Pointer-driven reorder for a pill strip — the pill you grab moves with the
 * cursor and its neighbours slide out of the way, the way a browser's tabs do.
 * Here rather than in either feature package for the same reason
 * `useTabOverview` is: the editor's tab bar (`features/workspace`) and the
 * agent's session tabs (`features/agent`) are both strips of `[data-tab-id]`
 * pills, and neither package may import the other.
 *
 * ── Why not HTML5 drag-and-drop ──────────────────────────────────────────
 * Under DnD the browser owns the drag image: the real pill stays put (dimmed
 * to say "this is the one"), a snapshot of it follows the cursor, and the two
 * read as a placeholder and a ghost rather than as one tab being moved. The
 * gesture needs the element itself, every frame.
 *
 * ── …while KEEPING it, because it is load-bearing elsewhere ─────────────
 * A tab dragged into the chat panel attaches its file, and one dragged onto
 * the browser's own tab bar opens it standalone — both are native drops, and
 * both would be lost by simply removing `draggable`. So the pills stay
 * draggable and this hook CLAIMS the gesture when it is a reorder: mostly
 * sideways is a reorder, anything else is a native drag.
 *
 * **The decision has to be made IN `dragstart`, from the pointer trail.** Two
 * facts force that shape, and each broke a version of this hook:
 *
 *   1. Once the browser opens a drag session it stops delivering pointer
 *      events. So a hook that waits for `pointermove` to cross a threshold
 *      never claims at all — the native drag runs, the thing under the cursor
 *      is the browser's drag image, and the neighbours never move.
 *   2. `dragstart.clientX/Y` is the drag ORIGIN, not where the pointer is now
 *      — it is within a pixel of the `pointerdown`. So the event cannot tell
 *      you the direction either, however carefully you read it.
 *
 * What does know is the last `pointermove` before the drag session opened, so
 * that is what `claimIfSideways` measures. Cancelling the `dragstart` puts
 * pointer events back and the drag proceeds as ours. The `pointermove` path
 * still claims on its own, for presses no drag session ever starts for.
 *
 * ── Landing ──────────────────────────────────────────────────────────────
 * On release the pill GLIDES into its slot and the reorder commits when it
 * arrives, so the list never re-renders under a moving element. Committing at
 * pointer-up instead would swap the pill to its new slot mid-flight — one
 * frame of the item in two places, which is the blink this shape avoids.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type RefObject } from 'react'
import { RAIL_SHIFT_MS, RAIL_SHIFT_TRANSITION } from './rail/spring.js'

/** Movement before a press is a reorder rather than a click or a native drag. */
const CLAIM_PX = 4
/** How long the released pill takes to reach its slot — the shift's own
 *  duration, IMPORTED rather than repeated: the commit fires when the glide
 *  ends, so a number that drifts from the CSS re-renders the list under a pill
 *  that is still moving. */
const LANDING_MS = RAIL_SHIFT_MS

/**
 * The strip's measured layout at the moment the drag was claimed. Positions
 * are the UNSHIFTED ones, so the arithmetic never chases its own output.
 *
 * Measured along whichever axis the strip RUNS: the editor's tab bar is a row,
 * but the agent's session tabs in a narrow panel wrap to one pill per row and
 * are a column. Same gesture, same arithmetic, one coordinate swapped — a
 * horizontal-only hook simply does nothing in the panel, since no pill is
 * beside any other.
 */
interface StripGeometry {
  /** `data-tab-id` per pill, captured at claim time — see `measure`. */
  ids: string[]
  axis: 'x' | 'y'
  /** Each pill's position ALONG the axis, and its extent along it. */
  mains: number[]
  sizes: number[]
  /** Its position ACROSS the axis — the line it sits on. A pill on another
   *  line must not move: a shift across a wrap points at a slot that is not
   *  there. */
  lines: number[]
  /** The gap between pills, measured rather than assumed — a constant here and
   *  a different one in the CSS leaves every pill short of its slot. */
  gap: number
}

export interface TabReorderState {
  /** The dragged pill's index in the list as it stands. */
  from: number
  /** The index it would occupy on release. */
  to: number
  /** Its live offset from home along the strip's axis, in px. */
  dx: number
  /** Which axis that offset is on. */
  axis: 'x' | 'y'
  /** True once released: gliding into `to`, with the commit still pending. */
  landing: boolean
}

export interface TabReorder {
  /** Attach to each pill's `onPointerDown`, with its index. */
  onPointerDown: (e: ReactPointerEvent<HTMLElement>, index: number) => void
  /** Call from `onDragStart`. Decides from the pointer TRAIL (never from the
   *  event, whose coordinates are the drag origin) whether this press is a
   *  reorder; true means it is, the drag has been claimed, and the native drag
   *  must be `preventDefault()`ed. */
  claimIfSideways: () => boolean
  /** Call from `onDragEnd`: the native drag has finished, so a press this hook
   *  never claimed can be dropped. */
  releaseToNativeDrag: () => void
  /** Call at the top of a pill's `onClick`: true — once — when the press that
   *  just ended was a drag and the click must be ignored. */
  consumeClick: () => boolean
  /** How far pill `i` is currently displaced along the strip's axis, in px. */
  offsetOf: (index: number) => number
  /** Everything a pill needs while a drag is in flight — the displacement, the
   *  transition (none on the one being dragged), and the lift. `{}` when idle,
   *  so it spreads onto any pill unconditionally. */
  styleFor: (index: number) => CSSProperties
  /** The pill being dragged, or null. */
  state: TabReorderState | null
}

/**
 * How far the dragged pill must reach INTO a neighbour before it takes that
 * neighbour's slot, as a fraction of the neighbour's size along the axis.
 *
 * The classic rule is centre-to-centre, and on a strip of equal pills that
 * means travelling a whole pill width PLUS the gap before anything moves — on
 * 120px tabs, ~124px of drag with the row sitting still, which reads as the
 * strip being stuck rather than as a threshold not yet met. Measuring the
 * dragged pill's LEADING EDGE into the neighbour instead makes room while the
 * gesture is still small: at 0.35 the same tabs open a slot after ~46px.
 *
 * It is a FRACTION of the neighbour, not a pixel constant, because tab widths
 * vary with their titles — a constant fires almost immediately against a wide
 * pill and barely at all against a narrow one, so the strip would behave
 * differently depending on what the tabs happen to be called.
 *
 * Lower is earlier. Below ~0.2 the slot opens under the pointer before the
 * user has expressed a direction, which reads as the strip twitching.
 */
export const ENTER_FRACTION = 0.35

/**
 * Where the dragged pill would land, given its live offset — walked outward
 * from home so a fast drag across several pills lands on the furthest one it
 * actually reached, not merely the next one.
 *
 * Pure and exported so the threshold is pinned by tests: it is the arithmetic
 * that decides how the gesture FEELS, and it is invisible to any component
 * test that only checks the final order.
 *
 * `g` is measured once at drag start and never re-measured, so the pills
 * shifting out of the way cannot feed back into this and oscillate.
 */
export function resolveTargetSlot(
  g: StripGeometry,
  from: number,
  dx: number,
  count: number,
): number {
  let to = from
  if (dx > 0) {
    // Leading edge is the pill's far side when travelling forward.
    const lead = g.mains[from]! + dx + g.sizes[from]!
    for (let i = from + 1; i < count && g.lines[i] === g.lines[from]; i++) {
      if (lead > g.mains[i]! + ENTER_FRACTION * g.sizes[i]!) to = i
    }
  } else {
    const lead = g.mains[from]! + dx
    for (let i = from - 1; i >= 0 && g.lines[i] === g.lines[from]; i--) {
      if (lead < g.mains[i]! + (1 - ENTER_FRACTION) * g.sizes[i]!) to = i
    }
  }
  return to
}

export function useTabReorder({
  stripRef,
  count,
  onReorder,
  enabled = true,
}: {
  /** The element that holds the pills; they are found by `[data-tab-id]`. */
  stripRef: RefObject<HTMLElement | null>
  count: number
  /** `movedId` / `targetId` are the pills' `data-tab-id` AS OF DRAG START.
   *  Prefer them over the indices: the commit lands LANDING_MS after release,
   *  and the list may have changed underneath (a tab closed elsewhere, a
   *  session pruned), which makes the indices name different tabs — or none. */
  onReorder: (from: number, to: number, ids: { movedId: string; targetId: string }) => void
  enabled?: boolean
}): TabReorder {
  const [state, setState] = useState<TabReorderState | null>(null)
  const press = useRef<{ index: number; pointerId: number; startX: number; startY: number } | null>(null)
  /** Where the pointer was at the last `pointermove` — the only reading of the
   *  gesture's direction available when `dragstart` fires. */
  const pointer = useRef<{ x: number; y: number } | null>(null)
  const geom = useRef<StripGeometry | null>(null)
  const claimed = useRef(false)
  const clickConsumed = useRef(false)
  const landingTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // The live state, for the window listeners — reading React state there would
  // capture whatever it was when the listener was attached.
  const live = useRef<TabReorderState | null>(null)
  const onReorderRef = useRef(onReorder)
  onReorderRef.current = onReorder

  const set = useCallback((next: TabReorderState | null) => {
    live.current = next
    setState(next)
  }, [])

  const measure = useCallback((): StripGeometry | null => {
    const strip = stripRef.current
    if (!strip) return null
    const pills = Array.from(strip.querySelectorAll<HTMLElement>('[data-tab-id]'))
    if (pills.length !== count || pills.length === 0) return null
    const rects = pills.map((el) => el.getBoundingClientRect())
    // The pills' identities AS THEY WERE when the gesture was claimed. The
    // commit happens LANDING_MS later and resolving positions against the
    // list as it stands then is a different question: a tab closed in another
    // pane, or a session pruned mid-glide, shifts every index after it — so
    // the wrong pair traded places, or the lookup produced `undefined` and the
    // reorder the user watched land was silently discarded.
    const ids = pills.map((el) => el.dataset.tabId ?? '')
    const tops = rects.map((r) => Math.round(r.top))
    // Every pill on its own row IS a column — that is the wrapped narrow panel,
    // where a sideways drag has nothing to trade places with.
    const stacked = pills.length > 1 && new Set(tops).size === pills.length
    const axis: 'x' | 'y' = stacked ? 'y' : 'x'
    const mains = rects.map((r) => (axis === 'x' ? r.left : r.top))
    const sizes = rects.map((r) => (axis === 'x' ? r.width : r.height))
    const lines = rects.map((r) => Math.round(axis === 'x' ? r.top : r.left))
    let gap = 0
    for (let i = 1; i < rects.length; i++) {
      if (lines[i] !== lines[i - 1]) continue
      gap = Math.max(0, Math.round(mains[i]! - (mains[i - 1]! + sizes[i - 1]!)))
      break
    }
    return { axis, mains, sizes, lines, gap, ids }
  }, [stripRef, count])

  const resolveTo = useCallback(
    (g: StripGeometry, from: number, dx: number): number => resolveTargetSlot(g, from, dx, count),
    [count],
  )

  /** The offset that puts the dragged pill exactly in slot `to`: the widths it
   *  jumped over, gaps included. Exact for variable-width pills, which a
   *  single "one pill over" cannot be. */
  const landingOffset = useCallback((g: StripGeometry, from: number, to: number): number => {
    let px = 0
    if (to > from) for (let i = from + 1; i <= to; i++) px += g.sizes[i]! + g.gap
    if (to < from) for (let i = to; i < from; i++) px -= g.sizes[i]! + g.gap
    return px
  }, [])

  /**
   * Take the gesture: past the threshold, and travelling along the strip
   * rather than across it. Both entry points funnel through here — `dragstart`
   * (the usual one; see the header) and `pointermove` (for a press the browser
   * never opens a drag session for).
   *
   * The strip has to be MEASURED to know which axis that is, so the geometry
   * is taken first and the direction judged against it. Measuring at claim
   * time rather than at pointerdown also means the strip is measured as it is
   * when the drag begins, not as it was when the finger landed.
   */
  const claim = useCallback((dx: number, dy: number, minPx = CLAIM_PX): boolean => {
    if (claimed.current) return true
    const g = measure()
    if (!g) return false
    const along = g.axis === 'x' ? dx : dy
    const across = g.axis === 'x' ? dy : dx
    if (Math.abs(along) < minPx || Math.abs(along) <= Math.abs(across)) return false
    geom.current = g
    claimed.current = true
    clickConsumed.current = true
    document.body.style.cursor = 'grabbing'
    document.body.style.userSelect = 'none'
    return true
  }, [measure])

  /** The native drag won this press (a pull toward the chat panel, say): no
   *  `pointerup` will be delivered, so the press is released here instead. */
  const releaseToNativeDrag = useCallback(() => {
    if (!claimed.current) press.current = null
  }, [])

  const clear = useCallback(() => {
    press.current = null
    claimed.current = false
    geom.current = null
    set(null)
  }, [set])

  useEffect(() => () => {
    if (landingTimer.current) clearTimeout(landingTimer.current)
    // Unmounting mid-drag (the panel closes, the route changes) otherwise
    // leaves `grabbing` / `user-select: none` on <body> for the rest of the
    // session — nothing else clears them, because the only cleanup is on the
    // drop path this never reaches. Only strip them if WE are still holding
    // them: a sibling drag may legitimately own them right now.
    if (claimed.current) {
      document.body.style.removeProperty('cursor')
      document.body.style.removeProperty('user-select')
    }
  }, [])

  useEffect(() => {
    if (!enabled) return
    const onMove = (e: PointerEvent) => {
      const p = press.current
      if (!p || e.pointerId !== p.pointerId) return
      pointer.current = { x: e.clientX, y: e.clientY }
      const dx = e.clientX - p.startX
      const dy = e.clientY - p.startY
      if (!claimed.current && !claim(dx, dy)) return
      const g = geom.current
      if (!g) return
      const along = g.axis === 'x' ? dx : dy
      set({ from: p.index, to: resolveTo(g, p.index, along), dx: along, axis: g.axis, landing: false })
    }
    const finishDrag = (commit: boolean) => {
      const p = press.current
      const g = geom.current
      const cur = live.current
      document.body.style.removeProperty('cursor')
      document.body.style.removeProperty('user-select')
      if (!p || !g || !cur) { clear(); return }
      const to = commit ? cur.to : p.index
      // Glide home (or into the new slot), THEN commit: the list must not
      // re-render while the pill is still travelling.
      set({ from: p.index, to, dx: landingOffset(g, p.index, to), axis: g.axis, landing: true })
      // RELEASE THE PRESS NOW, not when the timer commits. `pointermove` stays
      // bound for the whole landing, and people keep moving the mouse after
      // letting go — so a live press here meant `onMove` overwrote the landing
      // we just set with `dx: <live pointer>, landing: false`. The pill dropped
      // its glide and tracked the cursor UNTRANSITIONED (the grabbed pill only
      // gets a transition while `landing`), then jumped into place when the
      // timer fired. That is the "sticks to the cursor after release".
      //
      // `geom` must SURVIVE — `offsetOf` reads it every frame of the glide —
      // so this is not `clear()`; the timer still does that. Starting a new
      // drag mid-landing is already refused by `onPointerDown`'s `live.current`
      // guard, so dropping the press early cannot open one.
      press.current = null
      claimed.current = false
      landingTimer.current = setTimeout(() => {
        landingTimer.current = null
        if (to !== p.index) {
          onReorderRef.current(p.index, to, { movedId: g.ids[p.index] ?? '', targetId: g.ids[to] ?? '' })
        }
        clear()
      }, LANDING_MS)
    }
    const onUp = (e: PointerEvent) => {
      const p = press.current
      if (!p || e.pointerId !== p.pointerId) return
      if (!claimed.current) { press.current = null; return }  // a plain click
      finishDrag(true)
    }
    const onCancel = (e: PointerEvent) => {
      const p = press.current
      if (!p || e.pointerId !== p.pointerId) return
      finishDrag(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && claimed.current) finishDrag(false) }
    // A pointer released outside the window delivers no pointerup; a drag left
    // in flight would follow the next pointer that enters.
    const onBlur = () => { if (claimed.current) finishDrag(false) }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onCancel)
    window.addEventListener('keydown', onKey)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onCancel)
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('blur', onBlur)
    }
  }, [enabled, claim, resolveTo, landingOffset, clear, set])

  const offsetOf = (i: number): number => {
    const s = state
    const g = geom.current
    if (!s || !g) return 0
    if (i === s.from) return s.dx
    // Everything between the pill's home and the slot under it makes room by
    // one dragged-pill size. Same line only.
    if (g.lines[i] === undefined || g.lines[i] !== g.lines[s.from] || g.lines[s.to] !== g.lines[s.from]) return 0
    const step = g.sizes[s.from]! + g.gap
    if (i > s.from && i <= s.to) return -step
    if (i >= s.to && i < s.from) return step
    return 0
  }

  return {
    onPointerDown: (e, index) => {
      if (!enabled || e.button !== 0 || e.pointerType === 'touch') return
      if (live.current) return                       // a landing is still in flight
      press.current = { index, pointerId: e.pointerId, startX: e.clientX, startY: e.clientY }
      pointer.current = null
      claimed.current = false
      clickConsumed.current = false
    },
    releaseToNativeDrag,
    claimIfSideways: () => {
      const p = press.current
      const at = pointer.current
      if (!p || !at) return false
      const dx = at.x - p.startX
      const dy = at.y - p.startY
      // One pixel is enough here: the browser has already ruled this a drag,
      // so the only question left is which way it went. Waiting for the full
      // threshold would hand a slow sideways drag to the native path, since
      // that threshold may never be crossed while pointer events still flow.
      if (!claim(dx, dy, 1)) return false
      const g = geom.current!
      const along = g.axis === 'x' ? dx : dy
      // The pointer has already travelled by the time a drag session opens;
      // take that up now rather than on the next move.
      set({ from: p.index, to: resolveTo(g, p.index, along), dx: along, axis: g.axis, landing: false })
      return true
    },
    consumeClick: () => {
      if (!clickConsumed.current) return false
      clickConsumed.current = false
      return true
    },
    offsetOf,
    styleFor: (i) => {
      const s = state
      if (!s) return {}
      const offset = offsetOf(i)
      const grabbed = s.from === i
      return {
        transform: offset ? (s.axis === 'x' ? `translateX(${offset}px)` : `translateY(${offset}px)`) : undefined,
        // The GRABBED pill tracks the pointer with NO transition — a
        // transition on the dragged element is lag by definition. It gets one
        // only for the glide into its slot on release. Its neighbours always
        // animate; they are reacting, not being dragged.
        transition: grabbed ? (s.landing ? RAIL_SHIFT_TRANSITION : 'none') : RAIL_SHIFT_TRANSITION,
        // Above its neighbours as it passes over them.
        zIndex: grabbed ? 2 : undefined,
        position: grabbed ? 'relative' : undefined,
      }
    },
    state,
  }
}
